<template>
  <ValidationProvider
    v-slot="{ errors, invalid, dirty }"
    class="members"
    vid="user_organization_ids"
    :rules="`required|collectionsEmpty:${membersErrors}`"
    slim="slim"
  >
    <!-- eslint-disable vue/no-mutating-props -->
    <div
      id="user_organization_ids"
      class="form-group"
      :class="{ 'has-error': (invalid && dirty) || !!form.errors.user_organization_ids }"
    >
      <label class="control-label">Members</label>
      <div
        v-if="!disabled"
        class="label-secondary d-flex justify-content-between flex-column flex-sm-row"
      >
        <span>Members can only be on 1 overtime policy at a time</span>
        <a
          class="clickable"
          @click="selectAll"
        >
          Select all
        </a>
      </div>

      <AppSelect
        v-model="form.user_organization_ids"
        class="member-select mb-10"
        :options="membersOptions"
        :select2-options="select2Options"
        :disabled="disabled"
      />

      <div
        v-if="membersWithoutPayRates.length"
        class="policy-alert member-missing-rate"
      >
        <i class="hi hi-warning mr-10" />
        <div>
          Missing hourly pay rate:&nbsp;
          <span
            v-for="(member, index) in membersWithoutPayRates"
            :key="member.id"
            class="pay-rate-alert"
          >
            <a
              class="clickable"
              @click="openPayDialog(member)"
            >
              {{ member.name }}
            </a>
            <span v-if="index + 1 < membersWithoutPayRates.length">,&nbsp;</span>
          </span>
        </div>
      </div>

      <div
        v-if="removedMembers.length"
        class="policy-alert member-already-in-policy"
      >
        <i class="hi hi-info-filled mr-10" />
        {{ removedMemberNames }} will be removed from the policy at the start of the next work week. Members will still
        be able to track time to this policy until removed.
      </div>

      <div
        v-if="newMembers.length"
        class="policy-alert member-already-in-policy"
      >
        <i class="hi hi-info-filled mr-10" />
        To be added at the start of the next work week: {{ newMemberNames }}
      </div>

      <div
        v-for="policy in alreadyInPolicy"
        :key="policy.id"
        class="policy-alert member-already-in-policy"
      >
        <i class="hi hi-info-filled mr-10" />
        <span>
          To be switched from <strong> {{ policy.name }}</strong> at the start of the next work week:
          {{ policy.members.join(', ') }}
        </span>
      </div>

      <span
        v-if="errors[0] &amp;&amp; !form.errors.user_organization_ids"
        class="help-block"
      >
        {{ errors[0] }}
      </span>

      <span
        v-if="form.errors.user_organization_ids"
        class="help-block"
      >
        {{ form.errors.user_organization_ids[0] }}
      </span>
    </div>
    <!-- eslint-enable vue/no-mutating-props -->
  </ValidationProvider>
</template>

<script>
import { mapInertiaProps } from '~/utils/inertia';
import difference from 'lodash-es/difference';

export default {
  name: 'OvertimePoliciesMembersSelect',
  props: {
    allActivePolicies: {
      type: Array,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    members: {
      type: Array,
      required: true,
    },
    overtimePolicy: {
      type: Object,
      default: () => ({}),
    },
    overtimePolicies: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    newMemberWarning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      alreadyInPolicy: [],
      memberOfPolicy: [],
      membersWithoutPayRates: [],
      membersWithoutBillRates: [],
      membersWithoutPayAndBill: [],
      newMembers: [],
      removedMembers: [],
    };
  },
  computed: {
    ...mapInertiaProps({
      organization: 'organization',
    }),
    membersOptions() {
      return this.members.map(({ id, name }) => ({
        id,
        text: name,
      }));
    },
    select2Options() {
      return {
        allowClear: true,
        multiple: true,
        placeholder: 'Select members',
        width: '100%',
      };
    },
    membersErrors() {
      return this.membersWithoutPayRates;
    },
    newMemberNames() {
      return this.newMembers.map((m) => m.name).join(', ');
    },
    removedMemberNames() {
      return this.removedMembers.map((m) => m.name).join(', ');
    },
  },
  watch: {
    'form.user_organization_ids'(newVal) {
      this.checkSelected(newVal);
    },
    members() {
      this.checkSelected(this.form.user_organization_ids);
    },
  },
  methods: {
    selectAll() {
      // eslint-disable-next-line vue/no-mutating-props
      this.form.user_organization_ids = this.members.map((userOrg) => userOrg.id);
    },
    checkSelected(newValues) {
      this.membersWithoutPayRates = [];
      this.alreadyInPolicy = [];
      this.newMembers = [];
      this.removedMembers = [];

      const selectedValues = newValues.map((num) => parseInt(num));
      const selectedMemberData = this.members.filter(({ id }) => selectedValues.includes(id));

      // Determine which (if any) of selected members don't have a pay rate, bill rate, or both.
      // Currently, only the lack of pay rate is being shown in an alert since we are not allowing
      // bill rate multipliers.
      selectedMemberData.forEach((member) => {
        if (member.pay_rate === null) {
          this.membersWithoutPayRates.push(member);
        }

        if (member.bill_rate === null) {
          this.membersWithoutBillRates.push(member);
        }

        if (member.pay_rate === null && member.bill_rate === null) {
          this.membersWithoutPayAndBill.push(member);
        }

        if (this.newMemberWarning && !this.overtimePolicy.user_organization_ids.includes(member.id)) {
          this.newMembers.push(member);
        }
      });

      const removedMemberIds = difference(this.overtimePolicy.user_organization_ids, selectedValues);

      this.removedMembers = this.members.filter(({ id }) => removedMemberIds.includes(id));

      // Determine which (if any) of selected members are already added to a policy
      // If they are, an alert will be shown stating that they can't be selected
      const membersOfPolicy = this.allActivePolicies.filter(({ user_organization_ids }) =>
        user_organization_ids.some((id) => selectedValues.includes(id)),
      );

      membersOfPolicy.forEach((policy) => {
        if (this.overtimePolicy && this.overtimePolicy.id !== policy.id) {
          const policyMembers = [];

          policy.members = [];

          selectedMemberData.forEach((data) => {
            if (policy.user_organization_ids.includes(data.id)) {
              policy.members.push(data.name);
              policyMembers.push(data);
            }
          });

          if (policy.members.length) {
            this.alreadyInPolicy.push(policy);
            this.newMembers = difference(this.newMembers, policyMembers);
          }
        }
      });
    },
    openPayDialog(member) {
      this.$emit('open-pay-dialog', member);
    },
  },
};
</script>
